import { createContext, useContext, useMemo } from 'react'
import { useGetConfigurations } from '../hooks/configurations/index'
import type { QueryObserverResult, RefetchOptions } from '@tanstack/react-query'
import { Loader, MainContainer, PageContainer } from '@reapit/elements'
import { AccessLevel, Configurations, Permission, Privileges, Settings, SmsCreditsMenu, StaffMember } from '../types'

interface UserConfigurationsContextType {
  accessLevel: AccessLevel | undefined
  privileges: Privileges | undefined
  settings: Settings | undefined
  staffMember: StaffMember | undefined
  teams: any[] | undefined
  smsCreditsMenu: SmsCreditsMenu | undefined
  permissions: Permission[] | undefined
  refetchConfig: (options?: RefetchOptions) => Promise<QueryObserverResult<Configurations, Error>>
  loading: boolean
  isError: boolean
}

const UserConfigurationsContext = createContext<UserConfigurationsContextType | undefined>(undefined)

export const UserConfigurationsProvider = ({ children }: { children: React.ReactNode }) => {
  const { data, refetch, isLoading, isError, error } = useGetConfigurations()

  const value = useMemo(() => {
    const config = data?.response?.configuration

    return {
      accessLevel: config?.accessLevel,
      privileges: config?.privileges,
      settings: config?.settings,
      staffMember: config?.staffMember,
      teams: config?.teams,
      smsCreditsMenu: config?.smsCreditsMenu,
      permissions: config?.permissions,
      refetchConfig: refetch,
      loading: isLoading,
      isError: isError,
    }
  }, [data, refetch, isLoading, isError])

  if (isLoading) {
    return (
      <MainContainer hasGreyBackground>
        <PageContainer>
          <Loader fullPage />
        </PageContainer>
      </MainContainer>
    )
  }

  if (isError) {
    // Error handling goes here
    // Error container/snack/modal/alert
    console.error('Error for configurations', isError, error)
  }

  return <UserConfigurationsContext.Provider value={value}>{children}</UserConfigurationsContext.Provider>
}

export const useUserConfigurationsContext = () => {
  const context = useContext(UserConfigurationsContext)
  if (!context) {
    throw new Error('useUserConfigurationsContext must be used within an UserConfigurationsProvider')
  }
  return context
}
